import { PageModel } from '@psyomics/surveyjs-core';

import { TocCardState } from '../toc-card';

const findInProgressPage = (pages: PageModel[]) => {
  return pages.find((page) => !page.validate(false));
};

const filterTocs = (pages: PageModel[]) => {
  return pages.filter((page) => page.getPropertyValue('psyomicsToc'));
};

const NO_INDEX_FOUND = -1;

const nextTocIndex = (page: PageModel, pages: PageModel[]) => {
  const index = pages.findIndex((p) => p.visibleIndex > page.visibleIndex && p.getPropertyValue('psyomicsToc'));
  return index === NO_INDEX_FOUND ? pages.length : index;
};

const getPageState = (page: PageModel, pages: PageModel[], inProgressPage: PageModel | undefined): TocCardState => {
  const inProgressPageIndex = inProgressPage?.visibleIndex ?? pages.length;
  const nextTocIndexValue = nextTocIndex(page, pages);
  if (page.visibleIndex < inProgressPageIndex && inProgressPageIndex >= nextTocIndexValue) {
    return 'done';
  }
  if (page.visibleIndex <= inProgressPageIndex && inProgressPageIndex < nextTocIndexValue) {
    return 'in-progress';
  }
  return 'to-do';
};

type TocPage = {
  description: string;
  disabled: boolean;
  isCurrent: boolean;
  id: string;
  visibleIndex: number;
  onClick: () => void;
  state: TocCardState;
  title: string;
};

const generateTocs: (
  pages: PageModel[],
  goToPage: (page: PageModel) => (navToSectionState: ReturnType<typeof getPageState>) => void,
  currentPageVisibleIndex: number
) => TocPage[] = (pages, goToPage, currentPageVisibleIndex) => {
  const tocPageModels = filterTocs(pages);
  const inProgressPage = findInProgressPage(pages);
  const tocPages = tocPageModels.map((page) => {
    const state = getPageState(page, pages, inProgressPage);
    const handlePageNavigation = goToPage(page);

    return {
      description: page.description,
      disabled: state === 'to-do',
      id: page.id,
      isCurrent: false,
      onClick: () => handlePageNavigation(state),
      state,
      title: page.navigationTitle || page.title,
      visibleIndex: page.visibleIndex,
    };
  });

  const currentTocIndex = tocPages.findLastIndex((page) => page.visibleIndex <= currentPageVisibleIndex);

  return tocPages.map((toc, index) => ({
    ...toc,
    isCurrent: index === currentTocIndex,
  }));
};

export { generateTocs };
export type { TocCardState, TocPage };
